/**
 * Managing canned replies.
 *
 * WHY RETIRE RATHER THAN DELETE
 * A template someone has stopped using should leave the picker without
 * disappearing from history. Delete is still available, but it is the
 * destructive option and is presented as such — an admin-only action behind a
 * confirm, next to a one-click retire that does what people usually mean.
 *
 * WHY THE PLACEHOLDER LIST IS ON SCREEN
 * A placeholder nobody knows about is a placeholder nobody uses. The tokens and
 * what each falls back to when empty are listed next to the editor rather than
 * documented somewhere else, because "what happens if this customer has no
 * contact name" is the question an author needs answered while they write.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import ActionButton from "./ActionButton";

type Template = {
  id: number;
  name: string;
  category: string | null;
  body: string;
  default_internal: boolean;
  is_active: boolean;
  use_count: number;
};

type Placeholder = { token: string; fallback: string };

const BLANK = { name: "", category: "", body: "", default_internal: false };

export default function ReplyTemplateManager() {
  const [items, setItems] = useState<Template[]>([]);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [editing, setEditing] = useState<Template | null>(null);
  const [draft, setDraft] = useState({ ...BLANK });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // include_inactive so retired templates can be brought back; the picker
      // asks without it.
      const r = await api.get("/api/v1/ticket-templates", {
        params: { include_inactive: true },
      });
      setItems(r.data || []);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not load canned replies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api
      .get("/api/v1/ticket-templates/placeholders")
      .then((r) => setPlaceholders(r.data?.placeholders || []))
      .catch(() => setPlaceholders([]));
  }, [load]);

  const startNew = () => {
    setEditing(null);
    setDraft({ ...BLANK });
  };

  const startEdit = (t: Template) => {
    setEditing(t);
    setDraft({
      name: t.name,
      category: t.category || "",
      body: t.body,
      default_internal: t.default_internal,
    });
  };

  const save = async () => {
    setError("");
    if (!draft.name.trim() || !draft.body.trim()) {
      setError("A canned reply needs a name and a body.");
      return;
    }
    try {
      if (editing) {
        await api.put(`/api/v1/ticket-templates/${editing.id}`, draft);
      } else {
        await api.post("/api/v1/ticket-templates", draft);
      }
      startNew();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (t: Template) => {
    try {
      await api.put(`/api/v1/ticket-templates/${t.id}`, { is_active: !t.is_active });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not update that template");
    }
  };

  const remove = async (t: Template) => {
    if (
      !window.confirm(
        `Delete "${t.name}" permanently?\n\nIf you only want it out of the picker, ` +
          `retire it instead — that keeps it available to bring back.`,
      )
    )
      return;
    try {
      await api.delete(`/api/v1/ticket-templates/${t.id}`);
      if (editing?.id === t.id) startNew();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not delete that template");
    }
  };

  const insertToken = (token: string) =>
    setDraft((d) => ({ ...d, body: `${d.body}${token}` }));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
        Canned replies
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        The replies your team sends over and over. Technicians insert them from the ticket
        composer, already filled in for that customer and device.
      </p>

      {error && (
        <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ---- the list ---- */}
        <div>
          {loading && (
            <div className="text-xs text-gray-400 dark:text-gray-500">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              No canned replies yet.
            </p>
          )}
          <ul className="space-y-1.5">
            {items.map((t) => (
              <li
                key={t.id}
                className={`group flex items-center gap-2 rounded-lg border px-3 py-2 ${
                  t.is_active
                    ? "border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40"
                    : "border-dashed border-gray-200 dark:border-gray-700 opacity-60"
                } ${editing?.id === t.id ? "ring-2 ring-blue-400" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => startEdit(t)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                      {t.name}
                    </span>
                    {t.default_internal && (
                      <span className="text-[10px] rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 font-medium shrink-0">
                        Internal
                      </span>
                    )}
                    {!t.is_active && (
                      <span className="text-[10px] rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 shrink-0">
                        Retired
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    {t.category || "Other"}
                    {" · "}
                    {/* Which ones earn their place. A helpdesk accumulates
                        these and nobody can otherwise tell the three used daily
                        from the twenty written once. */}
                    used {t.use_count} {t.use_count === 1 ? "time" : "times"}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => toggleActive(t)}
                  className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 px-2 py-1 shrink-0"
                >
                  {t.is_active ? "Retire" : "Restore"}
                </button>
                <button
                  type="button"
                  onClick={() => remove(t)}
                  aria-label={`Delete ${t.name}`}
                  className="text-xs text-gray-400 hover:text-red-600 dark:hover:text-red-400 px-1.5 py-1 shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={startNew}
            className="mt-3 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            + New canned reply
          </button>
        </div>

        {/* ---- the editor ---- */}
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {editing ? `Editing "${editing.name}"` : "New canned reply"}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                Name
              </label>
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Acknowledge — we're on it"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                Category
              </label>
              <input
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                placeholder="Triage"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">
              Body
            </label>
            <textarea
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              rows={8}
              placeholder="Hi {{contact_first_name}},&#10;&#10;…"
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
            />
          </div>

          {placeholders.length > 0 && (
            <div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                Click to insert. Each falls back to neutral wording when the ticket has no
                value, so a reply never goes out with a gap in it.
              </div>
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map((p) => (
                  <button
                    key={p.token}
                    type="button"
                    onClick={() => insertToken(p.token)}
                    title={p.fallback ? `Falls back to "${p.fallback}"` : "Always available"}
                    className="text-[11px] font-mono rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700/40"
                  >
                    {p.token}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={draft.default_internal}
              onChange={(e) => setDraft({ ...draft, default_internal: e.target.checked })}
            />
            Insert as an internal note — for replies that are notes to the team, so the
            audience travels with the template
          </label>

          <div className="flex items-center gap-2">
            <ActionButton
              onAction={save}
              variant="raw"
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-1.5 rounded-lg"
              pendingLabel="Saving…"
            >
              {editing ? "Save changes" : "Create"}
            </ActionButton>
            {editing && (
              <button
                type="button"
                onClick={startNew}
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
